"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { HeaderBar, FuturIASidebar } from "@/components/futur-ia-oggi";
import {
  getAssistantWorkspace,
  sendAssistantRequest,
  type AssistantMessage,
  type AssistantPreview,
  type AssistantWorkspace,
} from "@/mock-api/ai-assistant";

type Conversation = { id: string; title: string; messages: AssistantMessage[]; draft: string; failedRequest: string | null };

export function FuturIAAssistant() {
  const [workspace, setWorkspace] = useState<AssistantWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [failedRequest, setFailedRequest] = useState<string | null>(null);
  const [activeClauseId, setActiveClauseId] = useState("tempi-consegna");
  const [mobileSurface, setMobileSurface] = useState<"chat" | "document">("chat");
  const [history, setHistory] = useState<Conversation[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [promptFeedback, setPromptFeedback] = useState("");
  const [conversationId, setConversationId] = useState("assistant-demo-initial");
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const historyCloseRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async (preview: AssistantPreview = "available") => {
    setLoading(true);
    setLoadError(false);
    try {
      const result = await getAssistantWorkspace(preview);
      setWorkspace(result);
      setMessages(result.initialMessages);
      setFailedRequest(null);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const mode = new URLSearchParams(window.location.search).get("stato");
    const preview: AssistantPreview = mode === "errore" ? "error" : mode === "vuoto" ? "empty" : "available";
    const timer = window.setTimeout(() => void load(preview), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!historyOpen) return;
    historyCloseRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setHistoryOpen(false);
        historyButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [historyOpen]);

  const activeClause = workspace?.clauses.find((clause) => clause.id === activeClauseId) ?? workspace?.clauses[1];
  const isEmpty = !loading && !loadError && messages.length === 0;
  const activeTitle = messages.find((message) => message.role === "user")?.content || draft.trim() || "Nuova conversazione";

  async function send(content: string, retry = false) {
    const text = content.trim();
    if (!text || sending || loading || loadError || !workspace) return;
    const requestConversationId = conversationId;
    setSending(true);
    setFeedback("");
    setPromptFeedback("");
    if (!retry) {
      setFailedRequest(null);
      setMessages((current) => [...current, {
        id: `assistant-user-${Date.now()}`,
        role: "user",
        content: text,
        createdAt: "09:12",
        status: "complete",
      }]);
    }
    try {
      const reply = await sendAssistantRequest(text, { retry });
      if (conversationId === requestConversationId) {
        setMessages((current) => [...current, reply]);
        setFailedRequest(null);
        setDraft((current) => current.trim() === text ? "" : current);
      } else {
        setHistory((current) => current.map((item) => item.id === requestConversationId ? {
          ...item,
          messages: [...item.messages, reply],
          draft: item.draft.trim() === text ? "" : item.draft,
          failedRequest: null,
        } : item));
      }
    } catch {
      if (conversationId === requestConversationId) {
        setFailedRequest(text);
        setDraft((current) => current || text);
      } else {
        setHistory((current) => current.map((item) => item.id === requestConversationId ? { ...item, failedRequest: text, draft: item.draft || text } : item));
      }
    } finally {
      setSending(false);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void send(draft);
  }

  function improvePrompt() {
    const content = draft.trim();
    if (!content) return;
    const request = content.replace(/[.!?]+$/, "");
    setDraft(`Per favore, ${request}. Rispondi in modo chiaro e pratico, indicando i passaggi principali.`);
    setPromptFeedback("Proposta locale inserita nel campo: puoi modificarla prima di inviare.");
  }

  function startNewChat() {
    if (messages.length || draft.trim()) {
      const title = messages.find((message) => message.role === "user")?.content || draft.trim() || "Nuova conversazione";
      setHistory((current) => [{ id: conversationId, title, messages: [...messages], draft, failedRequest }, ...current.filter((item) => item.id !== conversationId)]);
    }
    setConversationId(`assistant-chat-${Date.now()}`);
    setMessages([]);
    setDraft("");
    setFailedRequest(null);
    setFeedback("Nuova chat demo avviata. La conversazione precedente è disponibile in Cronologia.");
    setMobileSurface("chat");
  }

  function reopenConversation(conversation: Conversation) {
    if ((messages.length || draft.trim()) && conversationId !== conversation.id) {
      const title = messages.find((message) => message.role === "user")?.content || draft.trim() || "Conversazione demo";
      setHistory((current) => [{ id: conversationId, title, messages: [...messages], draft, failedRequest }, ...current.filter((item) => item.id !== conversationId)]);
    }
    setConversationId(conversation.id);
    setMessages(conversation.messages);
    setDraft(conversation.draft);
    setFailedRequest(conversation.failedRequest);
    setHistory((current) => current.filter((item) => item.id !== conversation.id));
    closeHistory();
    setFeedback("Conversazione demo ripresa.");
  }

  function closeHistory() {
    setHistoryOpen(false);
    historyButtonRef.current?.focus();
  }

  function selectClause(id: string) {
    setActiveClauseId(id);
    setMobileSurface("document");
  }

  function retryLoad() {
    if (typeof window !== "undefined" && window.location.search) window.history.replaceState(null, "", window.location.pathname);
    void load("available");
  }

  return <main className="futur-ia-app ai-assistant-page">
    <FuturIASidebar
      activeHref="/ai-assistant"
      assistantHistory={[
        { id: conversationId, title: activeTitle },
        ...history.map(({ id, title }) => ({ id, title })),
      ]}
      activeConversationId={conversationId}
      onNewConversation={startNewChat}
      onSelectConversation={(id) => {
        const conversation = history.find((item) => item.id === id);
        if (conversation) reopenConversation(conversation);
      }}
    />
    <section className="futur-ia-main">
      <HeaderBar onNotice={setFeedback} />
      <div className="futur-ia-content ai-assistant-content">
        <div className="futur-ia-heading-tools">
          <nav className="futur-ia-tabs" aria-label="Sezione FuturIA">
            <Link href="/">La mia giornata</Link>
            <Link aria-current="page" className="is-selected" href="/ai-assistant">Parla con FuturIA</Link>
          </nav>
        </div>
        <div className="ai-assistant-title-row">
          <div><h2>AI Assistant</h2><p>Demo · Risposte di esempio</p></div>
          <div className="ai-assistant-actions">
            <button type="button" onClick={startNewChat} disabled={sending}><span aria-hidden="true">＋</span> Nuova chat</button>
            <button ref={historyButtonRef} type="button" onClick={() => setHistoryOpen(true)}><span aria-hidden="true">◷</span> Cronologia{history.length > 0 && <span className="ai-history-count">{history.length}</span>}</button>
          </div>
        </div>
        {feedback && <div className="futur-ia-feedback ai-assistant-feedback" role="status"><span>{feedback}</span><button type="button" aria-label="Chiudi messaggio" onClick={() => setFeedback("")}>×</button></div>}
        {loadError && <div className="ai-assistant-state is-error" role="alert"><div><strong>La chat demo non è disponibile.</strong><span>La richiesta non è stata inviata a un servizio esterno.</span></div><button type="button" onClick={retryLoad}>Riprova</button></div>}
        {loading && <div className="ai-assistant-state" role="status"><span className="ai-loading-dot" />Caricamento della conversazione dimostrativa…</div>}

        {!loading && !loadError && workspace && <>
          <div className="ai-assistant-context"><span aria-hidden="true">▤</span><strong>{workspace.client}</strong><i>·</i><span>{workspace.document}</span><b>Demo</b></div>
          <div className="ai-assistant-mobile-tabs" role="group" aria-label="Superficie di lavoro">
            <button type="button" aria-pressed={mobileSurface === "chat"} className={mobileSurface === "chat" ? "is-selected" : ""} onClick={() => setMobileSurface("chat")}>Chat</button>
            <button type="button" aria-pressed={mobileSurface === "document"} className={mobileSurface === "document" ? "is-selected" : ""} onClick={() => setMobileSurface("document")}>Documento</button>
          </div>
          <div className="ai-assistant-workspace">
            <section className={`ai-chat-panel ${mobileSurface === "document" ? "mobile-hidden" : ""}`} aria-label="Conversazione dimostrativa">
              <div className="ai-chat-messages" aria-live="polite">
                {isEmpty && <div className="ai-assistant-empty"><span aria-hidden="true">✦</span><strong>La conversazione è vuota</strong><p>Scrivi una richiesta qui sotto per iniziare una chat dimostrativa sul contratto.</p></div>}
                {messages.map((message) => <article className={`ai-message ${message.role}`} key={message.id}>
                  {message.role === "assistant" && <span className="ai-message-mark" aria-hidden="true">✦</span>}
                  <div className="ai-message-body">
                    {message.role === "assistant" && message.id === "assistant-demo-intro" ? <>
                      <p>{message.content}</p><span className="ai-document-demo-label">▤ &nbsp;Documento dimostrativo</span>
                      <div className="ai-clause-list">{workspace.clauses.map((clause) => <button type="button" className={activeClauseId === clause.id ? "is-active" : ""} key={clause.id} onClick={() => selectClause(clause.id)} aria-pressed={activeClauseId === clause.id}><span className="ai-clause-radio" aria-hidden="true" /><span>{clause.title}</span><b aria-hidden="true">›</b></button>)}</div>
                      <Link href="/revisione" className="ai-review-link">Apri revisione <span aria-hidden="true">→</span></Link>
                    </> : <p>{message.content}</p>}
                    {message.role === "user" && <time>{message.createdAt}</time>}
                  </div>
                </article>)}
                {sending && <div className="ai-response-pending" role="status"><span className="ai-message-mark" aria-hidden="true">✦</span><span>Preparo una risposta dimostrativa…</span></div>}
                {failedRequest && <div className="ai-send-error" role="alert"><span>La risposta non è riuscita. La bozza è conservata nel composer.</span><button type="button" disabled={sending} onClick={() => void send(failedRequest, true)}>Riprova</button></div>}
              </div>
            </section>

            <section className={`ai-document-panel ${mobileSurface === "chat" ? "mobile-hidden" : ""}`} aria-label="Anteprima documento dimostrativo">
              <header className="ai-document-header"><span className="ai-document-icon" aria-hidden="true">▤</span><div><h3>{workspace.document}</h3><p>{workspace.documentStatus} · {workspace.version}</p></div><button type="button" aria-label="Dettagli documento demo" onClick={() => setFeedback("Documento e contenuto sono solo dimostrativi: nessuna bozza reale è stata modificata.")}>···</button></header>
              <div className="ai-document-body">
                <div className="ai-document-lines" aria-hidden="true"><i /><i /><i /></div>
                {activeClause && <article className="ai-document-clause" key={activeClause.id}><span className="ai-document-section-label">{activeClause.title}</span><h3>{activeClause.article}</h3><p>{activeClause.original}</p><p className="ai-proposal-label">Proposta dimostrativa</p><p className="ai-proposal-text">{activeClause.proposal}</p><p className="ai-clause-summary">{activeClause.summary}</p></article>}
                <div className="ai-document-lines ai-document-lines-bottom" aria-hidden="true"><i /><i /><i /></div>
              </div>
              {compareOpen && activeClause && <div className="ai-compare-panel" aria-live="polite"><div><strong>Confronto dimostrativo</strong><button type="button" aria-label="Chiudi confronto" onClick={() => setCompareOpen(false)}>×</button></div><p className="ai-compare-original"><b>Testo originale</b>{activeClause.original}</p><p className="ai-compare-proposed"><b>Proposta · non applicata</b>{activeClause.proposal}</p></div>}
              <footer className="ai-document-actions"><button type="button" className="ai-compare-button" aria-expanded={compareOpen} onClick={() => setCompareOpen((open) => !open)}><span aria-hidden="true">⇄</span>{compareOpen ? "Chiudi confronto" : "Confronta"}</button><button type="button" className="ai-save-button" onClick={() => setFeedback("Bozza salvata solo in questa demo visiva: nessun documento reale è stato modificato o persistito.")}>Salva bozza</button></footer>
            </section>
          </div>
        </>}
      </div>
      <div className="ai-assistant-composer-area">
        {draft.trim() && <button type="button" className="ai-improve-prompt" onClick={improvePrompt}><span aria-hidden="true">✦</span> Migliora prompt</button>}
        <form className="ai-assistant-composer" onSubmit={submit}>
          <textarea rows={1} value={draft} onChange={(event) => { setDraft(event.target.value); setPromptFeedback(""); }} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing && window.matchMedia("(min-width: 601px)").matches) { event.preventDefault(); void send(draft); } }} placeholder="Chiedi a FuturIA…" aria-label="Scrivi una richiesta demo" />
          <button type="submit" disabled={sending || loading || loadError || !draft.trim()} aria-label={sending ? "Risposta dimostrativa in corso" : "Invia richiesta demo"}>{sending ? "…" : "↑"}</button>
        </form>
        {promptFeedback && draft.trim() && <p className="ai-prompt-feedback" role="status">{promptFeedback}</p>}
        <p>Risposte di esempio · nessun provider AI o documento reale è collegato.</p>
      </div>
    </section>

    {historyOpen && <div className="ai-history-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeHistory(); }}><section className="ai-history-dialog" role="dialog" aria-modal="true" aria-labelledby="ai-history-heading"><header><div><span>Cronologia · demo</span><h2 id="ai-history-heading">Conversazioni recenti</h2></div><button ref={historyCloseRef} type="button" aria-label="Chiudi cronologia" onClick={closeHistory}>×</button></header>{history.length ? <ul>{history.map((conversation) => <li key={conversation.id}><button type="button" onClick={() => reopenConversation(conversation)}><strong>{conversation.title}</strong><span>{conversation.messages.length} messaggi · solo in questa sessione</span></button></li>)}</ul> : <div className="ai-history-empty"><span aria-hidden="true">◷</span><strong>Nessuna chat precedente</strong><p>Le conversazioni create qui saranno consultabili solo finché questa pagina resta aperta.</p></div>}<p className="ai-history-disclaimer">Cronologia dimostrativa · non viene salvata dopo il ricaricamento.</p></section></div>}
  </main>;
}
